(() => {
  const lang = (document.documentElement.lang || '').toLowerCase();
  if (lang === 'en' || lang.startsWith('en-')) return;

  /*
   * BRG storefront rule
   * -------------------
   * Product option names and option values must ALWAYS be shown in English,
   * regardless of the storefront language. Only visible labels are replaced:
   * underlying localized values/IDs stay untouched so Shopify's variant logic
   * continues to work.
   */

  const productCache = new Map();
  const pickerDataCache = new Map();
  const selectedPickerDataCache = new Map();

  const handleFromUrl = rawUrl => {
    if (!rawUrl) return null;
    try {
      const url = new URL(rawUrl, window.location.origin);
      const parts = url.pathname.split('/').filter(Boolean);
      const productIndex = parts.lastIndexOf('products');
      return productIndex >= 0 ? parts[productIndex + 1] : null;
    } catch (_) {
      return null;
    }
  };

  const englishProduct = handle => {
    if (!handle) return Promise.resolve(null);
    if (!productCache.has(handle)) {
      // English is the BRG store's primary Shopify locale, rooted at '/'.
      // Using '/' intentionally bypasses /it, /fr, /de and /es.
      productCache.set(handle, fetch(`/products/${encodeURIComponent(handle)}.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      }).then(response => {
        if (!response.ok) throw new Error('English product data unavailable');
        return response.json();
      }).catch(error => {
        productCache.delete(handle);
        throw error;
      }));
    }
    return productCache.get(handle);
  };

  const optionName = (product, index) => {
    const option = product?.options?.[index];
    return typeof option === 'string' ? option : option?.name;
  };

  const uniqueOptionValues = (product, index) => {
    const values = [];
    (product?.variants || []).forEach(variant => {
      const value = variant?.options?.[index];
      if (value != null && !values.includes(value)) values.push(value);
    });
    return values;
  };

  const cleanLabelText = label => {
    if (!label) return '';
    const clone = label.cloneNode(true);
    clone.querySelectorAll('.visually-hidden').forEach(node => node.remove());
    return clone.textContent.replace(/\s+/g, ' ').trim();
  };

  const parseEnglishPickerHtml = html => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const picker = doc.querySelector('variant-selects, variant-radios');
    if (!picker) return [];
    const groups = [...picker.querySelectorAll('.product-form__input--dropdown, .product-form__input--radios')];
    return groups.map(group => {
      const heading = group.querySelector('.product-form__group-name');
      const name = (heading?.textContent || '').replace(/:\s*$/, '').trim();
      const select = group.querySelector('select.select__select, select[name^="options["]');
      if (select) {
        const values = [...select.options].map(option => option.textContent.replace(/\s+/g, ' ').trim());
        const selectedIndex = Math.max(0, select.selectedIndex);
        return { name, values, selected: values[selectedIndex] };
      }
      const radios = [...group.querySelectorAll('input[type="radio"]')];
      const values = radios.map(input => {
        const label = [...group.querySelectorAll('label')].find(candidate => candidate.htmlFor === input.id);
        if (label?.classList.contains('color-swatch')) {
          return (label.querySelector('.visually-hidden')?.textContent || '').trim();
        }
        return cleanLabelText(label);
      });
      const selectedIndex = radios.findIndex(input => input.checked);
      return { name, values, selected: selectedIndex >= 0 ? values[selectedIndex] : undefined };
    });
  };

  const englishPickerData = (handle, variantId = null) => {
    if (!handle) return Promise.resolve([]);
    const cache = variantId ? selectedPickerDataCache : pickerDataCache;
    const key = variantId ? `${handle}:${variantId}` : handle;
    if (!cache.has(key)) {
      const suffix = variantId ? `?variant=${encodeURIComponent(variantId)}` : '';
      cache.set(key, fetch(`/products/${encodeURIComponent(handle)}${suffix}`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'text/html' }
      }).then(response => {
        if (!response.ok) throw new Error('English product page unavailable');
        return response.text();
      }).then(parseEnglishPickerHtml).catch(error => {
        cache.delete(key);
        throw error;
      }));
    }
    return cache.get(key);
  };

  const parsePickerVariants = picker => {
    const json = picker.querySelector('script[type="application/json"]');
    if (!json) return [];
    try { return JSON.parse(json.textContent || '[]'); } catch (_) { return []; }
  };

  const buildValueMaps = (localizedVariants, english, optionCount) => {
    const englishById = new Map((english?.variants || []).map(v => [String(v.id), v]));
    const maps = Array.from({ length: optionCount }, () => new Map());
    (localizedVariants || []).forEach(localVariant => {
      const englishVariant = englishById.get(String(localVariant?.id));
      if (!englishVariant) return;
      for (let i = 0; i < optionCount; i += 1) {
        const localValue = localVariant?.options?.[i];
        const englishValue = englishVariant?.options?.[i];
        if (localValue != null && englishValue != null && !maps[i].has(String(localValue))) {
          maps[i].set(String(localValue), String(englishValue));
        }
      }
    });
    return maps;
  };

  const englishValue = (map, localValue, fallbackValues, fallbackIndex) => {
    const mapped = map?.get(String(localValue));
    if (mapped != null) return mapped;
    if (fallbackValues?.[fallbackIndex] != null) return fallbackValues[fallbackIndex];
    return localValue;
  };

  const setText = (node, value) => {
    if (!node || value == null) return;
    const next = String(value);
    if (node.textContent !== next) node.textContent = next;
  };

  const setHeading = (node, value) => {
    if (!node || !value) return;
    const hadColon = node.textContent.trim().endsWith(':');
    const next = String(value) + (hadColon ? ':' : '');
    if (node.textContent.trim() !== next) node.textContent = next;
  };

  const labelForInput = (group, input) => {
    if (!input?.id) return null;
    return [...group.querySelectorAll('label')].find(label => label.htmlFor === input.id) || null;
  };

  const setVisibleRadioLabel = (label, value) => {
    if (!label || value == null) return;
    if (label.classList.contains('color-swatch')) {
      setText(label.querySelector('.visually-hidden'), value);
      label.setAttribute('aria-label', String(value));
      return;
    }
    const textNode = [...label.childNodes].find(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
    if (textNode) {
      if (textNode.textContent.trim() !== String(value)) textNode.textContent = `\n\t\t\t${value}\n\t\t\t`;
    } else {
      label.prepend(document.createTextNode(String(value)));
    }
  };

  const patchPicker = async picker => {
    if (!picker?.isConnected) return;
    const handle = handleFromUrl(picker.dataset.url) || handleFromUrl(window.location.pathname);
    if (!handle) return;

    try {
      const [english, pageGroups] = await Promise.all([
        englishProduct(handle),
        englishPickerData(handle).catch(() => [])
      ]);
      if (!english || !picker.isConnected) return;

      const localizedVariants = parsePickerVariants(picker);
      const groups = [...picker.querySelectorAll('.product-form__input--dropdown, .product-form__input--radios')];
      const valueMaps = buildValueMaps(localizedVariants, english, groups.length);

      groups.forEach((group, index) => {
        const englishName = pageGroups[index]?.name || optionName(english, index);
        // The HTML fallback is important for products with >250 variants because
        // Shopify's Ajax Product API caps the variants array at 250.
        const fallbackValues = pageGroups[index]?.values?.length
          ? pageGroups[index].values
          : uniqueOptionValues(english, index);
        setHeading(group.querySelector('.product-form__group-name'), englishName);

        const select = group.querySelector('select.select__select, select[name^="options["]');
        if (select) {
          [...select.options].forEach((option, valueIndex) => {
            const display = englishValue(valueMaps[index], option.value, fallbackValues, valueIndex);
            setText(option, display);
          });

          const visibleDisplay = group.querySelector('.brg-option-text');
          if (visibleDisplay) {
            const syncDisplay = () => {
              const currentIndex = Math.max(0, select.selectedIndex);
              const currentOption = select.options[currentIndex];
              const display = englishValue(valueMaps[index], currentOption?.value, fallbackValues, currentIndex);
              setText(visibleDisplay, display);
            };
            syncDisplay();
            if (!select.dataset.brgEnglishListener) {
              select.dataset.brgEnglishListener = '1';
              select.addEventListener('change', () => requestAnimationFrame(syncDisplay));
            }
          }
        }

        const radios = [...group.querySelectorAll('input[type="radio"]')];
        radios.forEach((input, valueIndex) => {
          const display = englishValue(valueMaps[index], input.value, fallbackValues, valueIndex);
          setVisibleRadioLabel(labelForInput(group, input), display);
        });
      });
    } catch (_) {
      // Retry later via the MutationObserver if the English source is temporarily unavailable.
    }
  };

  const englishVariantData = async (handle, variantId) => {
    const english = await englishProduct(handle);
    const variant = (english?.variants || []).find(v => String(v.id) === String(variantId));
    if (variant) {
      return {
        product: english,
        names: (english.options || []).map((_, index) => optionName(english, index)),
        values: variant.options || [],
        title: variant.title
      };
    }

    // For variants beyond the Ajax API 250-variant cap, render the English
    // product page with ?variant=ID and read the selected English values.
    const groups = await englishPickerData(handle, variantId);
    const names = groups.map(group => group.name);
    const values = groups.map(group => group.selected);
    return {
      product: english,
      names,
      values,
      title: values.filter(Boolean).join(' / ')
    };
  };

  const patchCartItem = async item => {
    if (!item?.isConnected) return;
    const handle = item.dataset.brgProductHandle || handleFromUrl(item.querySelector('a[href*="/products/"]')?.href);
    const variantId = item.dataset.brgVariantId;
    if (!handle || !variantId) return;
    try {
      const data = await englishVariantData(handle, variantId);
      if (!item.isConnected) return;
      item.querySelectorAll('[data-brg-variant-option-index]').forEach(row => {
        const index = Number(row.dataset.brgVariantOptionIndex);
        setHeading(row.querySelector('dt'), data.names?.[index]);
        setText(row.querySelector('dd'), data.values?.[index]);
      });
    } catch (_) {}
  };

  const patchPickup = async node => {
    if (!node?.isConnected) return;
    const handle = node.dataset.brgProductHandle;
    const variantId = node.dataset.brgVariantId;
    if (!handle || !variantId) return;
    try {
      const data = await englishVariantData(handle, variantId);
      if (!node.isConnected) return;
      node.querySelectorAll('[data-brg-variant-option-index]').forEach(row => {
        const index = Number(row.dataset.brgVariantOptionIndex);
        setText(row.querySelector('[data-brg-option-name]'), data.names?.[index]);
        setText(row.querySelector('[data-brg-option-value]'), data.values?.[index]);
      });
    } catch (_) {}
  };

  const patchVariantTitle = async node => {
    if (!node?.isConnected) return;
    const handle = node.dataset.brgProductHandle;
    const variantId = node.dataset.brgVariantId;
    if (!handle || !variantId) return;
    try {
      const data = await englishVariantData(handle, variantId);
      if (data.title) setText(node, data.title);
    } catch (_) {}
  };

  const patchQuickView = async root => {
    if (!root?.isConnected) return;
    const href = document.querySelector('#sca-qv-detail')?.getAttribute('href');
    const handle = handleFromUrl(href);
    if (!handle) return;
    try {
      const [english, pageGroups] = await Promise.all([
        englishProduct(handle),
        englishPickerData(handle).catch(() => [])
      ]);
      if (!english || !root.isConnected) return;
      const groups = [...root.querySelectorAll('.selector-wrapper, .sca-qv-optionrow')]
        .filter(group => group.querySelector('select'));
      groups.forEach((group, index) => {
        const name = pageGroups[index]?.name || optionName(english, index);
        setHeading(group.querySelector('label'), name);
        const values = pageGroups[index]?.values?.length
          ? pageGroups[index].values
          : uniqueOptionValues(english, index);
        const select = group.querySelector('select');
        [...(select?.options || [])].forEach((option, valueIndex) => {
          if (values[valueIndex] != null) setText(option, values[valueIndex]);
        });
      });
    } catch (_) {}
  };

  const patchAll = root => {
    const scope = root?.querySelectorAll ? root : document;

    if (scope.matches?.('variant-selects, variant-radios')) patchPicker(scope);
    scope.querySelectorAll?.('variant-selects, variant-radios').forEach(patchPicker);

    if (scope.matches?.('[data-brg-product-handle][data-brg-variant-id].cart-item')) patchCartItem(scope);
    scope.querySelectorAll?.('[data-brg-product-handle][data-brg-variant-id].cart-item').forEach(patchCartItem);

    if (scope.matches?.('.pickup-availability-variant[data-brg-product-handle]')) patchPickup(scope);
    scope.querySelectorAll?.('.pickup-availability-variant[data-brg-product-handle]').forEach(patchPickup);

    if (scope.matches?.('[data-brg-english-variant-title]')) patchVariantTitle(scope);
    scope.querySelectorAll?.('[data-brg-english-variant-title]').forEach(patchVariantTitle);

    const quickView = document.querySelector('#sca-qv-variant-options');
    if (quickView) patchQuickView(quickView);
  };

  let scheduled = false;
  const schedulePatch = root => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      patchAll(root || document);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => patchAll(document), { once: true });
  } else {
    patchAll(document);
  }

  document.addEventListener('shopify:section:load', event => patchAll(event.target));
  document.addEventListener('change', event => {
    if (event.target.closest?.('variant-selects, variant-radios, #sca-qv-variant-options')) schedulePatch(document);
  }, true);

  const observer = new MutationObserver(() => schedulePatch(document));
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
